import auth from './authManager';
import store from './store/index';

const apiClient = {
  /**
   * this object contains all the endpoints available from apis
   */
  calls(context) {
    async function makeCall(verb, endpoint, data, config) {
      let response = null;

      switch (verb) {
        case 'GET':
          response = await context.axios.get(endpoint, data, config);
          break;

        case 'POST':
          response = await context.axios.post(endpoint, data, {
            withCredentials: true,
            xsrfCookieName: 'XSRF-TOKEN',
            xsrfHeaderName: 'X-XSRF-TOKEN',
            ...config,
          });
          break;

        case 'PUT':
          response = await context.axios.put(endpoint, data);
          break;

        case 'DELETE':
          response = await context.axios.delete(endpoint);
          break;

        default:
          response = await context.axios.get(endpoint);
      }

      return response.data ? response.data.data : null;
    }

    return {
      getLoginData: async () => makeCall('GET', '/auth/login'),
      loginWithUsernamePassword: async data =>
        makeCall('POST', '/auth/login', data),
      recoverPassword: async data => makeCall('POST', '/auth/recover', data),
      getProfile: async () => makeCall('GET', '/profile'),
      updateProfile: async data => makeCall('POST', '/profile', data),
      getDevices: async () => makeCall('GET', '/profile/devices'),
      getPlans: async () => makeCall('GET', '/profile/plans'),
      checkUser: async (field, value) =>
        makeCall('POST', '/profile/check', { field, value }),

      // tickets
      getTickets: async () => makeCall('GET', '/tickets'),
      getCustomerTickets: async () => makeCall('GET', '/tickets/customer'),
      createTicket: async data => makeCall('POST', '/tickets', data),
      updateTicket: async (id, data) => makeCall('PUT', `/tickets/${id}`, data),
      getAgendas: async (type_id, start, end) =>
        makeCall('GET', '/tickets/slots'),
      getSlots: async (type_id, start, end, format = 'slot') =>
        makeCall('GET', `/tickets/slots/${type_id}`, {
          params: { start, end, format },
        }),

      // sentinel
      getSentinels: async () => makeCall('GET', '/sentinels'),
      getSentinel: async id => makeCall('GET', `/sentinels/${id}`),

      getAdditionalnfo: async (id, key) =>
        makeCall('GET', `/sentinels/${id}/additional_info/${key}`),
      setAdditionalnfo: async (id, key, value) =>
        makeCall('POST', `/sentinels/${id}/additional_info`, { key, value }),
      getSentinelSpeedtest: async id =>
        makeCall('GET', `/sentinels/${id}/speedtests`),
      getSentinelPings: async id => makeCall('GET', `/sentinels/${id}/pings`),
      getSentinelTraffic: async id =>
        makeCall('GET', `/sentinels/${id}/traffic`),

      getSentinelDevicesOverall: async id =>
        makeCall('GET', `/sentinels/${id}/devices/overall`),
      getSentinelDevices: async id =>
        makeCall('GET', `/sentinels/${id}/devices`),
      getSentinelDevicesDiscoveries: async id =>
        makeCall('GET', `/sentinels/${id}/devices/discoveries`),
      getSentinelDevice: async (id, device_id) =>
        makeCall('GET', `/sentinels/${id}/devices/${device_id}`),
      updateSentinelDevice: async (id, device_id, data) =>
        makeCall('POST', `/sentinels/${id}/devices/${device_id}`, data),

      getSentinelDeviceMonitoringSettings: async (id, device_id) =>
        makeCall('GET', `/sentinels/${id}/devices/${device_id}/monitoring`),
      updateSentinelDeviceMonitoringSettings: async (id, device_id, data) =>
        makeCall(
          'POST',
          `/sentinels/${id}/devices/${device_id}/monitoring`,
          data,
        ),

      getSentinelDeviceTraffic: async (id, device_id) =>
        makeCall(
          'GET',
          `/sentinels/${id}/devices/${device_id}/trafficaggregate`,
        ),
      getSentinelDeviceSettings: async (id, device_id) =>
        makeCall('GET', `/sentinels/${id}/devices/${device_id}/settings`),
      setSentinelDeviceSettings: async (id, device_id, data) =>
        makeCall(
          'POST',
          `/sentinels/${id}/devices/${device_id}/settings`,
          data,
        ),

      getAllEvents: async () => makeCall('GET', '/sentinels/events'),
      getSentinelEvents: async id => makeCall('GET', `/sentinels/${id}/events`),
      getSentinelEventsByType: async (id, type) =>
        makeCall('GET', `/sentinels/${id}/events/type/${type}`),
      getSentinelEventsByDevice: async (id, device) =>
        makeCall('GET', `/sentinels/${id}/events/device/${device}`),

      getSentinelNetworkSettings: async id =>
        makeCall('GET', `/sentinels/${id}/settings/network`),
      setSentinelNetworkSettings: async (id, settings) =>
        makeCall('POST', `/sentinels/${id}/settings/network`, settings),
      getSentinelMonitoringSettings: async id =>
        makeCall('GET', `/sentinels/${id}/settings/monitoring`),
      setSentinelMonitoringSettings: async (id, settings) =>
        makeCall('POST', `/sentinels/${id}/settings/monitoring`, settings),
      getSentinelAlarmsSettings: async id =>
        makeCall('GET', `/sentinels/${id}/settings/alarms`),
      setSentinelAlarmsSettings: async (id, settings) =>
        makeCall('POST', `/sentinels/${id}/settings/alarms`, settings),

      getSentinelsTickets: async () => makeCall('GET', '/sentinels/tickets'),
      getSentinelsTicketsArchive: async () =>
        makeCall('GET', '/sentinels/tickets/archive'),
      getSentinelsTicketsData: async () =>
        makeCall('GET', '/sentinels/tickets/data'),
      createSentinelsTicket: async ticket_data =>
        makeCall('POST', '/sentinels/tickets/create', ticket_data),

      getSentinelTickets: async id =>
        makeCall('GET', `/sentinels/${id}/tickets`),
      getSentinelTicket: async (id, ticket_id) =>
        makeCall('GET', `/sentinels/${id}/tickets/${ticket_id}`),
      updateSentinelTicket: async (id, ticket_id, ticket_data) =>
        makeCall('POST', `/sentinels/${id}/tickets/${ticket_id}`, ticket_data),
      getSentinelTicketsArchive: async id =>
        makeCall('GET', `/sentinels/${id}/tickets/archive`),
    };
  },

  /**
   * Attach to current Vue instance
   * @param Vue
   * @param options
   */
  install(Vue, options) {
    if (options.baseURL) {
      console.log(`[apiclient] Setting base URL to ${options.baseURL}`);
      Vue.axios.defaults.baseURL = options.baseURL;
    }
    Vue.prototype.$apiclient = this.calls(Vue);
    Vue.axios.defaults.headers.common.Authorization = `Bearer ${auth.getToken()}`;

    store.watch(
      state => state.auth.user,
      (user) => {
        // sending command to web socket when ot session is ready
        if (user !== null) {
          console.log(`Updating user token: ${auth.getToken()}`);
          Vue.axios.defaults.headers.common.Authorization = `Bearer ${auth.getToken()}`;
        }
      },
    );

    // errors interceptor
    Vue.axios.interceptors.response.use(
      response => response,
      (error) => {
        if (!error.response) {
          Vue.$router.push('/error');
          return Promise.reject(error);
        }

        console.info(`Http Call Response. Status: ${error.response.status}`);

        switch (error.response.status) {
          case 401:
            console.warn('Auth token expired, redirect to login');
            auth.logout();
            break;
        }

        // Do something with response error
        return Promise.reject(error.response.data);
      },
    );

    // Setting loading on request, then close it
    Vue.axios.interceptors.request.use(
      (config) => {
        NProgress.start();
        return config;
      },
      (error) => {
        NProgress.done();
        return Promise.reject(error);
      },
    );

    Vue.axios.interceptors.response.use(
      (response) => {
        NProgress.done();
        return response;
      },
      (error) => {
        NProgress.done();
        return Promise.reject(error);
      },
    );
  },
};

export default apiClient;



// WEBPACK FOOTER //
// ./src/apiClient.js